(ns common.ui.add-item
  (:require ["@mui/material/Autocomplete$default" :as Autocomplete]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.spec.alpha :as spec]
            [common.widgets.form-field :refer [form-field]]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui add-button
  [{:keys [current-value]
    :add-item/keys [add-fn add-button-props add-button-label
                    ignore-input? valid?]
    :or {add-button-label "Add"}}]
  ($ Button
     (merge
      {:onClick (fn [_]
                  (add-fn current-value))
       :data-testid :add-item-button
       :disabled (or (and (some? valid?) (not (valid? current-value)))
                     (and (not ignore-input?) (nil? current-value)))
       :startIcon ($ Icon "add")}
      add-button-props)
     add-button-label))

(defui dropdown [{:add-item/keys [select-options dropdown-props add-fn] :as cfg}]
  (let [[value set-value] (uix/use-state (first select-options))
        select-options (use-subscribe select-options)]
    (uix/use-effect (fn []
                      (when (or (nil? value)
                                (not (contains? (set select-options) value)))
                        (set-value (first select-options))))
                    [value select-options])
    ($ Stack
       {:direction "row"
        :alignItems "start"
        :gap 1}
       ($ Autocomplete
          (merge
           {:size "small"
            :options (to-array select-options)
            :getOptionLabel (fn [option]
                              (if (map? option)
                                (:label option)
                                option))
            :value value
            :onChange (fn [_e ^js value]
                        (set-value value))
            :renderInput (fn [^js props]
                           ($ TextField props))
            :renderOption (fn [^js props option _state]
                            (when-let [on-click (:on-click option)]
                              (set! (.-onClick props) on-click))
                            ($ :li props
                               ($ Stack
                                  {:direction "row"
                                   :alignItems "center"
                                   :gap 1
                                   :sx #js {:flex 1
                                            :overflow "hidden"}}
                                  ($ Icon {:fontSize "small"} (:icon option))
                                  ($ Typography
                                     {:component "span"
                                      :sx #js {:flex 1}}
                                     (:label option))
                                  (when-let [chip (:chip option)]
                                    ($ Chip
                                       {:label chip
                                        :color "highlight"
                                        :sx #js {:fontWeight 500}})))))
            :sx #js {:flex 1}}
           dropdown-props))
       ($ add-button
          (merge cfg
                 {:add-item/add-fn (comp add-fn :value)
                  :current-value value})))))

(defui no-input [{:add-item/keys [read-only?] :as cfg}]
  (when-not read-only?
    ($ add-button (merge cfg {:current-value nil}))))

(rf/reg-sub-raw ::view-sub-1
  (fn [_ [_ disabled-sub]]
    (r/reaction
     (when disabled-sub
       @(rf/subscribe disabled-sub)))))

(defui view [{:add-item/keys [label select-options ignore-input? read-only? value-editor disabled-sub] :as cfg}]
  (let [disabled? (use-subscribe [::view-sub-1 disabled-sub])
        merge-read-only (fn [cfg m] (merge-with merge cfg (when (or disabled? read-only?) m)))
        cfg (merge-read-only cfg {:add-item/add-button-props {:disabled true}})
        input-control
        (cond ignore-input?
              ($ no-input (merge cfg {:key cfg}))

              (some? select-options)
              ($ dropdown (merge-read-only cfg {:key cfg :add-item/dropdown-props {:disabled true}}))

              (some? value-editor)
              ^{:key cfg}
              (r/as-element
               [value-editor (merge-read-only cfg {:add-item/input-props {:disabled true}})]))]
    ($ form-field
       {:label label}
       input-control)))

(spec/def ::existing-ids
  (spec/or :attribute qualified-keyword?
           :collection (spec/coll-of string?)
           :subscription :reframe/sub-or-event))

(defn- generate-suggested-id
  "Get the highest n within existing-ids where n is found in existing-id pattern
   `{prefix}-{n}` and increment this by 1. Return 1 when `n` can not be found."
  [prefix existing-ids]
  (let [pattern (re-pattern (str "^" prefix "-([\\d]+)$"))]
    (str prefix "-"
         (inc
          (reduce (fn [acc s]
                    (let [n (-> (re-find pattern s) second int)]
                      (if (> n acc) n acc))) 0 existing-ids)))))

(defn generate-unique-id [existing-ids prefix]
  (case (first (spec/conform ::existing-ids existing-ids))
    :attribute
    (generate-unique-id [:ui.store/values-by-attr existing-ids] prefix)

    :subscription
    ;; the subscription should return a collection of strings
    (generate-unique-id @(rf/subscribe existing-ids) prefix)

    :collection
    (if-not (contains? (set existing-ids) prefix)
      prefix
      (let [suggested-id (generate-suggested-id prefix existing-ids)]
        (if-not (contains? (set existing-ids) suggested-id)
          suggested-id
          (generate-unique-id (conj existing-ids suggested-id) prefix))))

    prefix))

(rf/reg-sub :add-item/suggest-name
  (fn [[_ attr _prefix]]
    (rf/subscribe [:ui.store/values-by-attr attr]))
  (fn [existing-names [_ _attr prefix]]
    (generate-unique-id existing-names prefix)))
